import { Component, createRef, KeyboardEvent } from 'react'
import * as cn from 'classnames'
import * as find from 'lodash/find'
import * as debounce from 'lodash/debounce'
import * as styles from 'styles/atoms/inputs/OldSelect.css'
import { Option, ErrorItem, InputTheme, QAMarked } from 'interfaces/component'
import DropdownItems from 'components/atoms/inputs/DropdownItems'
import TextField from 'components/atoms/inputs/TextField'

export type OldSelectProps<TValue> = QAMarked & {
  options: Option<TValue>[]
  label: string

  active: boolean
  value: null | TValue

  errors?: ErrorItem[]
  errored?: boolean
  touched?: boolean
  isLoading?: boolean

  hint?: string
  theme: InputTheme
  withBorder?: boolean

  autoFocus?: boolean
  allowOuterScroll?: boolean

  onChange: (value: TValue) => void
  onFocus: () => void
  onBlur: () => void
}

type OldSelectState = {
  isDropdownShown: boolean
  searchText: string
  active: boolean
}

/**
 * @deprecated
 * Use Select with react-hook-form
 */

class OldSelect<TValue> extends Component<OldSelectProps<TValue>, OldSelectState> {
  static defaultProps: Partial<OldSelectProps<void>> = {
    theme: 'white',
  }

  static getDerivedStateFromProps (nextProps: OldSelectProps<any>, prevState: OldSelectState) {
    const nextActive = nextProps.active
    const prevActive = prevState.active

    const activeChanged = nextActive !== prevActive
    if (!activeChanged) {
      return {}
    }

    return {
      isDropdownShown: nextActive,
      active: nextActive,
    }
  }

  state = {
    isDropdownShown: false,
    searchText: '',
    active: this.props.active,
  }

  private dropdownRef = createRef<DropdownItems<TValue>>()

  render () {
    const {
      options, value, label, hint,
      active, onFocus, onBlur,
      errors, errored, touched,
      theme, isLoading, qaMarker,
      allowOuterScroll, autoFocus,
      withBorder,
    } = this.props
    const { isDropdownShown, searchText } = this.state

    const selected = find(options, { value }) || null
    const isDisabled = options.length === 0

    // TS magic and bugs: remove "any" when TS will manage to generics in jsx
    const Dropdown: new () => DropdownItems<TValue> = DropdownItems as any

    return (
      <div
        className={ cn(styles.select, {
          [styles.isDisabled]: isDisabled,
          [styles.isDropdownShown]: isDropdownShown,
          [styles.isLoading]: isLoading,
        }) }
        onClick={ this.handleClick }
      >
        <TextField
          readOnly
          active={ active }
          disabled={ isDisabled }
          label={ label }
          hint={ hint }
          value={ selected ? selected.label : '' }
          ellipsis={ isDropdownShown ? 'none' : 'show' }
          ellipsisTheme={ theme }
          errors={ errors }
          errored={ errored }
          touched={ touched }
          isLoading={ isLoading }
          onFocus={ onFocus }
          onBlur={ onBlur }
          onKeyDown={ this.handleKeyDown }
          qaMarker={ qaMarker }
          qaType='select'
          autoFocus={ autoFocus }
        />
        <Dropdown
          ref={ this.dropdownRef }
          isShown={ isDropdownShown }
          options={ options }
          selected={ selected }
          onSelect={ this.handleSelect }
          searchText={ searchText }
          allowOuterScroll={ allowOuterScroll }
          darkTheme={ theme === 'dark' }
          qaMarker={ qaMarker }
          withBorder={ withBorder }
        />
      </div>
    )
  }

  private setDropdownVisibility (isDropdownShown: boolean) {
    this.setState({ isDropdownShown })
  }

  private shouldSearchByChar (keyCode: number) {
    return keyCode === 32 ||                    // space
      keyCode >= 48 && keyCode <= 90 ||         // 0 - z
      keyCode >= 186 && keyCode <= 192 ||       // ; - `
      keyCode >= 219 && keyCode <= 222          // [ - '
  }

  private resetSearchText = () => {
    this.setState({
      searchText: '',
    })
  }

  private resetSearchTextDebounced = debounce(this.resetSearchText, 1000)

  private handleClick = () => {
    const { active } = this.props
    const { isDropdownShown } = this.state

    if (active && !isDropdownShown) {
      this.setDropdownVisibility(true)
    }
  }

  private handleSelect = (option: Option<TValue>) => {
    const { onChange } = this.props

    onChange(option.value)
    this.setDropdownVisibility(false)
  }

  private handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const { isDropdownShown } = this.state
    const dropdown = this.dropdownRef.current
    if (!dropdown) {
      return
    }

    switch (event.key) {
      case 'Enter':
        if (isDropdownShown) {
          dropdown.handleEnter()
        } else {
          this.setDropdownVisibility(true)
        }
        event.preventDefault()
        return

      case 'Escape':
        if (isDropdownShown) {
          this.setDropdownVisibility(false)
        }
        event.preventDefault()
        return

      case 'ArrowUp':
        if (!isDropdownShown) {
          this.setDropdownVisibility(true)
        }
        dropdown.handleArrow('up')
        event.preventDefault()
        return

      case 'ArrowDown':
        if (!isDropdownShown) {
          this.setDropdownVisibility(true)
        }
        dropdown.handleArrow('down')
        event.preventDefault()
        return

      default:
        if (this.shouldSearchByChar(event.keyCode)) {
          this.handleSearchByChar(event.key)
          event.preventDefault()
        }
        return
    }
  }

  private handleSearchByChar = (char: string) => {
    this.setState((prevState) => ({
      searchText: prevState.searchText + char.toLowerCase(),
    }))
    this.resetSearchTextDebounced()
  }
}

export default OldSelect
